import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { SalesHistoryRow } from '@sellokart/shared';
import { PageHeader } from '@/components/PageHeader';
import { AsyncSection } from '@/components/AsyncSection';
import { ReportFilterBar } from '@/components/ReportFilterBar';
import {
  ReportColumnsButton,
  ReportFiltersButton,
  ReportPeriodButton,
  ReportSearchField,
  ReportToolbar,
} from '@/components/ReportToolbar';
import { applyReportLayout, DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { TablePager } from '@/components/TablePager';
import { fetchSalesHistory, reportKeys } from '@/features/reports/api';
import { useReportFilters } from '@/features/reports/useReportFilters';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { useReportToolbar } from '@/features/reports/useReportToolbar';
import { usePortal } from '@/features/portal/PortalContext';
import { formatPosDateTime, saleDateTime } from '@/lib/format';

const PAGE_SIZE = 25;

const money = (value: number) => value.toFixed(2);

// Taxable Amount / Tax % are derived here rather than stored: the base a
// sale's tax was charged on is subtotal minus its discount, and the rate is
// back-computed from what was actually charged (taxAmount ÷ taxable) rather
// than trusting a separately-stored percentage that could drift from the
// amount — same reasoning the POS's own report applies.
function taxableAmount(row: SalesHistoryRow): number {
  return row.subtotal - row.discountAmount;
}
function taxPercent(row: SalesHistoryRow): number {
  const taxable = taxableAmount(row);
  return taxable > 0 ? (row.taxAmount / taxable) * 100 : 0;
}

/**
 * Fields the Filters popover offers. These names are the API's contract —
 * `SALES_HISTORY_FILTER_COLUMNS` maps each to a column server-side, and
 * anything not in that map is refused rather than queried.
 */
const FILTER_FIELDS = ['Customer', 'Doc #', 'Status'] as const;

/** Toggleable columns (Date is pinned — it carries the row's identity). */
const TOGGLEABLE = [
  'docNumber',
  'customerName',
  'trn',
  'taxPercent',
  'taxableAmount',
  'taxAmount',
  'status',
  'grandTotal',
] as const;

const COLUMN_LABELS: Record<string, string> = {
  docNumber: 'Doc #',
  customerName: 'Customer',
  trn: 'TRN',
  taxPercent: 'Tax %',
  taxableAmount: 'Taxable Amount',
  taxAmount: 'Tax Amount',
  status: 'Status',
  grandTotal: 'Final Price',
};

const COLUMNS: Column<SalesHistoryRow>[] = [
  { key: 'date', header: 'Date', pinned: true, cell: (row) => formatPosDateTime(saleDateTime(row)) },
  { key: 'docNumber', header: 'Doc #', cell: (row) => row.docNumber },
  { key: 'customerName', header: 'Customer', cell: (row) => row.customerName || 'Walk-in' },
  { key: 'trn', header: 'TRN', visibility: 'hidden md:table-cell', cell: (row) => row.trn },
  { key: 'taxPercent', header: 'Tax %', align: 'right', cell: (row) => `${taxPercent(row).toFixed(1)}%` },
  { key: 'taxableAmount', header: 'Taxable Amount', align: 'right', cell: (row) => money(taxableAmount(row)) },
  { key: 'taxAmount', header: 'Tax Amount', align: 'right', cell: (row) => money(row.taxAmount) },
  { key: 'status', header: 'Status', visibility: 'hidden sm:table-cell', cell: (row) => row.status },
  { key: 'grandTotal', header: 'Final Price', align: 'right', cell: (row) => money(row.grandTotal) },
];

/**
 * Paginated list, server-side (`page`/`pageSize`), because a tenant can
 * accumulate far more sales than receivables/payables rows — unlike those
 * point-in-time lists, this one is not safe to fetch whole and filter client-side.
 *
 * Route is top-level (`/sales-history`, not `/reports/sales-history`) to match
 * the flat scheme every other portal report page already uses (`/sales`,
 * `/shifts`, `/ledger` in `App.tsx`; `/receivables` already stubbed in
 * `PortalLayout`'s nav).
 */
export function SalesHistoryPage() {
  const { profile } = usePortal();
  const navigate = useNavigate();
  const [filters, setFilters] = useReportFilters();
  const [page, setPage] = useState(1);
  // Any toolbar change resets to page 1 — page 3 of the previous result
  // set is meaningless once the predicate changes.
  const toolbar = useReportToolbar(filters, setFilters, () => setPage(1));
  const layout = useReportLayout('sales-history', TOGGLEABLE);
  const columns = applyReportLayout(COLUMNS, layout);

  const query = useQuery({
    queryKey: reportKeys.salesHistory(filters, page, PAGE_SIZE),
    queryFn: ({ signal }) => fetchSalesHistory(filters, page, PAGE_SIZE, signal),
  });

  return (
    <div>
      <PageHeader title="Sales history" description="Every recorded sale for the selected window." />
      <ReportFilterBar
        filters={filters}
        onChange={(next) => {
          setPage(1);
          setFilters(next);
        }}
        branches={profile?.branches ?? []}
        showDocType
        hideDateRange
      />
      <ReportToolbar>
        <ReportSearchField
          value={toolbar.search}
          onChange={toolbar.setSearch}
          placeholder="Search doc # or customer"
        />
        <ReportFiltersButton
          fields={FILTER_FIELDS}
          applied={toolbar.filterRows}
          matchAny={toolbar.matchAny}
          onApply={toolbar.applyFilters}
          placeholderFor={(field) =>
            field === 'Customer' ? 'name' : field === 'Status' ? 'Active / Void' : 'doc #'
          }
        />
        <ReportColumnsButton layout={layout} labels={COLUMN_LABELS} />
        <ReportPeriodButton
          from={filters.from}
          to={filters.to}
          onChange={toolbar.setRange}
          deselected={toolbar.filterActive}
        />
      </ReportToolbar>
      <AsyncSection
        state={query}
        isEmpty={(paged) => paged.items.length === 0}
        emptyTitle="No sales in this window"
        skeleton={<TableSkeleton columns={5} />}
      >
        {(paged) => (
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <DataTable
              caption="Sales history"
              layout={layout}
              columns={columns}
              rows={paged.items}
              rowKey={(row) => `${row.deviceId}-${row.sourceId}`}
              onRowClick={(row) => navigate(`/sales-history/${row.deviceId}/${row.sourceId}`)}
            />
            <TablePager
              page={paged.page}
              pageSize={paged.pageSize}
              total={paged.total}
              onPageChange={setPage}
              busy={query.isFetching}
              noun="sale"
            />
          </div>
        )}
      </AsyncSection>
    </div>
  );
}
